import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import {AIStarIcon, Button, Dialog, Flex, Title} from '@priority-software/priority-style-react';
import {OptimizationDialogContent} from './OptimizationDialogContent';
import styles from './OptimizationDialog.module.scss';
import {OptimizationDialogConsiderOption, OptimizationDialogOpenValue} from './';
import {apiLocker, Form, formsStore, Record} from '../../../../stores/Form';
import {formatDate} from '../../../Form/utils/date';
import {common, priority} from '@priority-software/priority-proto-api/lib';
import {createApi} from '@priority-software/priority-geoservice-api';
import grpcWeb from 'grpc-web';
import {getAuthorizationHeader, getBaseURL} from '../../../../services/rest/utils';
import {DataTestIds} from '../../constants';
import {selectCompanyStore} from '../../../SelectCompanyDlg/SelectCompanyStore';
import {texts} from '../../texts';
import {CloseWithHelp} from '../../../ReportGenerator/componenets/CloseWithHelp';
import {helpPopoverRef} from '../../../../_shared/Help/constants';
import {RouteType} from '../../types';
import {optimizeDeliveryOrderForRoutes} from '../../actions/action';
import {showErrorMessage} from '../../../../_shared/Messages/actions/MessageActions';
import {showErrorToast, showSuccessToast} from '../../../../components/Toast/actions/ToastActions';
import {OptimizationLoaderState} from '../OptimizationLoader';
import {getCurrentEntitySession} from '../../../../_shared/GWTBridge/invoke/genPage';
import {generateRouteRequestData, generateUnassignedRequestData, RequestData} from './requests';
import {UtilizationValue, UtilizationWarning} from './UtilizationWarning';
import {Dictionary} from '../../../EOYWizard/types';
import {
    Action,
    getStorageKey,
    Namespace,
    StoreProps
} from '../../../../services/proto/ServerStorage/utils';
import {
    getServerStoreData,
    setServerStoreData
} from '../../../../services/proto/ServerStorage/service';

export type OptimizationDialogProps = {
    zIndex: number;
    openValue: OptimizationDialogOpenValue;
    setOpenValue: (isOpen: OptimizationDialogOpenValue) => void;
    optimizationDialogState: OptimizationDialogState;
    setOptimizationDialogState: React.Dispatch<React.SetStateAction<OptimizationDialogState>>;
    assignToDeliveryHandler: (date: string) => Promise<void>;
    planningForm?: Form;
    routesForm?: Form;
    routes: RouteType[];
    selectedPlannedRows: Record[];
    selectedRoutes: RouteType[];
    optimizationLoaderState: OptimizationLoaderState;
    setOptimizationLoaderState: (value: OptimizationLoaderState) => void;
    processBatchRoutesReloadData: (data: Dictionary<Dictionary<Form>>) => Promise<void>;
    session: string;
    dname: string;
    setIsScreenLocked: (value: boolean) => void;
    reloadData: (openValue: OptimizationDialogOpenValue, ids?: string[]) => Promise<void>;
};

export type OptimizationDialogState = {
    runOnlyOnSelected: boolean;
    optimizeByDistance: boolean;
    constraintsConsideration: boolean;
    timeWindowConsideration: boolean;
    unassignedOptimization: boolean;
    startLocationId?: number;
    consider: OptimizationDialogConsiderOption;
};

export type OptimizationDialogRefType = {closeStream: () => void};
const areToastsEnabled = false;

const keyParams: StoreProps = {
    nameSpace: Namespace.System,
    action: Action.onStorage,
    storage: 'ADP_optimization'
};

const getStorageName = (openValue: OptimizationDialogOpenValue) => {
    return (
        keyParams.storage +
        '_' +
        (openValue === OptimizationDialogOpenValue.Open ? 'route' : 'unassigned')
    );
};

export const OptimizationDialog = forwardRef<OptimizationDialogRefType, OptimizationDialogProps>(
    (
        {
            zIndex,
            openValue,
            setOpenValue,
            optimizationDialogState,
            setOptimizationDialogState,
            assignToDeliveryHandler,
            planningForm,
            selectedPlannedRows,
            setOptimizationLoaderState,
            selectedRoutes: selectedRoutesProp,
            routesForm,
            routes,
            processBatchRoutesReloadData,
            session,
            dname,
            setIsScreenLocked,
            reloadData
        },
        ref
    ) => {
        const [deliveryDateValue, setDeliveryDateValue] = useState<string>(
            formatDate(new Date(), priority.netitems.form.ColumnType.Date8)
        );
        const [lastOpenValue, setLastOpenValue] = useState<OptimizationDialogOpenValue>();
        const [isModalStateLoaded, setIsModalStateLoaded] = useState<boolean>(false);

        useEffect(() => {
            if (openValue === OptimizationDialogOpenValue.Closed) {
                if (lastOpenValue !== undefined) {
                    setDeliveryDateValue(
                        formatDate(new Date(), priority.netitems.form.ColumnType.Date8)
                    );
                    const key = getStorageKey({
                        ...keyParams,
                        storage: getStorageName(lastOpenValue)
                    });
                    const storeMap = {
                        [key]: new common.Constant({
                            Str: JSON.stringify({
                                ...optimizationDialogState,
                                startLocationId: undefined
                            })
                        })
                    };
                    setServerStoreData(storeMap, true, true);
                    setIsModalStateLoaded(false);
                }
            } else {
                const key = getStorageKey({
                    ...keyParams,
                    storage: getStorageName(openValue)
                });
                getServerStoreData([key], true, true)
                    .then(x => {
                        if (x[key]?.Str) {
                            try {
                                setOptimizationDialogState(
                                    JSON.parse(x[key].Str) as OptimizationDialogState
                                );
                            } catch (err) {
                                console.error(
                                    'Failed to parse optimization dialog state from storage:',
                                    err,
                                    x[key].Str
                                );
                            }
                        }
                    })
                    .finally(() => {
                        setIsScreenLocked(false);
                        setIsModalStateLoaded(true);
                    });
            }
            setLastOpenValue(openValue);
        }, [openValue]);

        const streamRef = useRef<ReturnType<ReturnType<typeof createApi>['optimizeRoute']>>(null);
        const abortControllerRef = useRef<AbortController>(null);

        const isRunDisabled =
            deliveryDateValue === '' &&
            openValue === OptimizationDialogOpenValue.UnassignedOpen &&
            optimizationDialogState.unassignedOptimization;

        const closeStream = () => {
            streamRef.current?.removeAllListeners();
            abortControllerRef.current?.abort();
        };

        useImperativeHandle(ref, () => ({
            closeStream
        }));

        useEffect(() => {
            return () => {
                closeStream();
            };
        }, []);

        const selectedRoutes = useRef<RouteType[]>([]);
        const [dataLoadingFlag, setDataLoadingFlag] = useState<boolean>(false);
        const [dataReadyFlag, setDataReadyFlag] = useState(false);
        const [utilizationValue, setUtilizationValue] = useState<UtilizationValue | undefined>(
            undefined
        );
        const [utilizationNumericValue, setUtilizationNumericValue] = useState<number>();

        useEffect(() => {
            if (openValue === OptimizationDialogOpenValue.Closed) {
                setUtilizationValue(undefined);
                setUtilizationNumericValue(undefined);
            } else {
                selectedRoutes.current = selectedRoutesProp;
            }
        }, [openValue]);

        useEffect(() => {
            if (
                dataReadyFlag &&
                (openValue === OptimizationDialogOpenValue.Open
                    ? !routes.find(x =>
                          optimizationDialogState.runOnlyOnSelected
                              ? !x.locations && x.isSelected
                              : !x.locations
                      )
                    : true)
            ) {
                runGeoServiceOptimization(routes);
                setDataReadyFlag(false);
            }
        }, [routes, dataReadyFlag]);

        const getUtilizationInfo = useCallback(async () => {
            const API = createApi(
                `${getBaseURL()}/geoservice`,
                selectCompanyStore?.getPrettyName(),
                getAuthorizationHeader(),
                'ADP'
            );

            streamRef.current = API.optimizeRoute([], [], [], undefined, undefined);

            streamRef.current.on('data', x => {
                console.log('Received from geoservice', x);
                const value = x.Summary.LicenseUtilization;
                const code = x.Summary.Code;
                setUtilizationNumericValue(value);
                if (['Finished', 'Failed'].includes(x.Status.State)) {
                    if (value < 0.8) {
                        setUtilizationValue(UtilizationValue.NoWarning);
                    } else if (value >= 0.8 && value < 1) {
                        setUtilizationValue(UtilizationValue.Warning);
                    }
                    if (code >= 1000) {
                        if (code === 1010) {
                            if (openValue === OptimizationDialogOpenValue.UnassignedOpen) {
                                setUtilizationValue(UtilizationValue.ExceedsNumber);
                                setOptimizationDialogState({
                                    ...optimizationDialogState,
                                    unassignedOptimization: false
                                });
                            } else {
                                showErrorMessage({
                                    content: texts.optimization_exceed_limit,
                                    confirmButtonText: texts.ok_button,
                                    onConfirm: () => {},
                                    onClose: () => {}
                                });
                                setIsScreenLocked(false);
                                setOpenValue(OptimizationDialogOpenValue.Closed);
                                setUtilizationValue(UtilizationValue.ExceedsNumber);
                            }
                        } else {
                            setUtilizationValue(UtilizationValue.GracePeriod);
                        }
                    }
                }
            });
        }, [
            setUtilizationValue,
            openValue,
            optimizationDialogState,
            setOpenValue,
            setOptimizationDialogState
        ]);

        const runGeoServiceOptimization = useCallback(
            async (routes: RouteType[]) => {
                const API = createApi(
                    `${getBaseURL()}/geoservice`,
                    selectCompanyStore?.getPrettyName(),
                    getAuthorizationHeader(),
                    'ADP'
                );

                setOpenValue(OptimizationDialogOpenValue.Closed);
                setOptimizationLoaderState(OptimizationLoaderState.VisibleCancellable);

                let result: RequestData;

                try {
                    if (
                        selectedPlannedRows.length > 0 &&
                        openValue === OptimizationDialogOpenValue.UnassignedOpen
                    ) {
                        if (abortControllerRef.current) {
                            abortControllerRef.current.abort();
                        }

                        abortControllerRef.current = new AbortController();

                        await new Promise(async (resolve, reject) => {
                            const abortListener = () => {
                                formsStore.currentForm?.abortController?.abort();
                                routesForm.abortController?.abort();
                                abortControllerRef.current.signal.removeEventListener(
                                    'abort',
                                    abortListener
                                );
                                apiLocker.unlockSession(formsStore.currentForm?.session);
                                reject();
                            };
                            abortControllerRef.current.signal.addEventListener(
                                'abort',
                                abortListener
                            );

                            try {
                                result = await generateUnassignedRequestData(
                                    selectedPlannedRows,
                                    deliveryDateValue,
                                    optimizationDialogState,
                                    dname
                                );
                            } catch (e) {
                                console.log(e);
                                reject(undefined);
                            }
                            resolve(undefined);
                        });
                    } else if (openValue === OptimizationDialogOpenValue.Open) {
                        result = await generateRouteRequestData(
                            routesForm,
                            routes,
                            selectedRoutes.current,
                            optimizationDialogState
                        );
                    }
                } catch {
                    setDataLoadingFlag(false);
                    setOptimizationLoaderState(OptimizationLoaderState.Hidden);
                    return;
                }

                try {
                    streamRef.current = API.optimizeRoute(
                        result.locations,
                        result.jobs,
                        result.vehicles,
                        undefined,
                        result.options
                    );

                    streamRef.current
                        .on('data', x => {
                            console.log('Received from geoservice', x);
                            if (x.Status.State === 'Finished') {
                                setOptimizationLoaderState(OptimizationLoaderState.Visible);
                                optimizeDeliveryOrderForRoutes(
                                    x,
                                    openValue === OptimizationDialogOpenValue.UnassignedOpen,
                                    dname
                                )
                                    .then(() => {
                                        setOptimizationLoaderState(OptimizationLoaderState.Hidden);
                                        if (
                                            getCurrentEntitySession() !== session &&
                                            areToastsEnabled
                                        ) {
                                            showSuccessToast(texts.optimization_toast_success);
                                        }

                                        const routesToProcess = routes.filter(x =>
                                            optimizationDialogState.runOnlyOnSelected
                                                ? selectedRoutes.current
                                                      .map(y => y.id)
                                                      .includes(x.id)
                                                : true
                                        );

                                        reloadData(
                                            openValue,
                                            (openValue === OptimizationDialogOpenValue.Open
                                                ? routesToProcess
                                                : routes.filter(x => !!x.locationSubform)
                                            ).map(
                                                x =>
                                                    x.data.getField(
                                                        routesForm.getColumnByName('DLVTRACKNUM')
                                                            ?.id
                                                    )?.currentValue
                                            ) as string[]
                                        );
                                    })
                                    .catch(() => {
                                        setOptimizationLoaderState(OptimizationLoaderState.Hidden);
                                        if (
                                            getCurrentEntitySession() !== session &&
                                            areToastsEnabled
                                        ) {
                                            showErrorToast(texts.optimization_toast_error);
                                        }
                                    });
                            } else if (x.Status.State === 'Failed') {
                                let content = '';
                                switch (x.Summary.Code) {
                                    case 400:
                                    case 401:
                                    case 404:
                                    case 429:
                                    case 500:
                                    case 1408:
                                    case 1429:
                                        content = texts['optimization_unexpected_issue'];
                                        break;
                                    case 403:
                                        content = texts['optimization_unexpected_issue_consult'];
                                        break;
                                    case 413:
                                        content = texts['optimization_unexpected_issue_narrowing'];
                                        break;
                                    case 422:
                                        content = texts['optimization_unexpected_issue_check'];
                                        break;
                                    default:
                                        content = x.Summary.Description;
                                        break;
                                }
                                showErrorMessage({
                                    onConfirm: () => {},
                                    confirmButtonText: texts.close_tooltip,
                                    content
                                });
                            }
                        })
                        .on('error', (error: any) => {
                            if (error.code !== grpcWeb.StatusCode.OK) {
                                setOptimizationLoaderState(OptimizationLoaderState.Hidden);
                                if (getCurrentEntitySession() !== session && areToastsEnabled) {
                                    showErrorToast(texts.optimization_toast_error);
                                } else {
                                    showErrorMessage({
                                        onConfirm: () => {},
                                        confirmButtonText: texts.close_tooltip,
                                        content: texts['optimization_unexpected_issue_check']
                                    });
                                }
                            }
                        });
                } catch {
                    setDataLoadingFlag(false);
                    setOptimizationLoaderState(OptimizationLoaderState.Hidden);
                    return;
                }
                setDataLoadingFlag(false);
            },
            [selectedPlannedRows, openValue, optimizationDialogState, deliveryDateValue, reloadData]
        );

        const onAutoPlanningRun = useCallback(async () => {
            await assignToDeliveryHandler(deliveryDateValue);
        }, [deliveryDateValue, assignToDeliveryHandler]);

        const onRunButtonClick = async () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();
            return new Promise(async (resolve, reject) => {
                const abortListener = () => {
                    abortControllerRef.current.signal.removeEventListener('abort', abortListener);
                    routesForm.abortController?.abort();
                    reject();
                };
                abortControllerRef.current.signal.addEventListener('abort', abortListener);
                if (
                    openValue === OptimizationDialogOpenValue.UnassignedOpen
                        ? optimizationDialogState.unassignedOptimization
                        : true
                ) {
                    setDataLoadingFlag(true);
                    setOptimizationLoaderState(OptimizationLoaderState.VisibleCancellable);
                    setDataReadyFlag(true);
                } else {
                    await onAutoPlanningRun();
                }
                resolve(undefined);
            });
        };

        useEffect(() => {
            if (openValue === OptimizationDialogOpenValue.Open) {
                setOptimizationDialogState(state => ({
                    ...state,
                    runOnlyOnSelected: selectedRoutes.current.length !== 0
                }));
            }
        }, [isModalStateLoaded]);

        useEffect(() => {
            if (
                openValue !== OptimizationDialogOpenValue.Closed &&
                utilizationValue === undefined
            ) {
                setIsScreenLocked(true);
                getUtilizationInfo();
            }
        }, [openValue]);

        return utilizationValue !== undefined && isModalStateLoaded ? (
            <Dialog
                dataTestId={DataTestIds.optimizationDialogId}
                maskClosable
                isOpen={openValue !== OptimizationDialogOpenValue.Closed && !dataLoadingFlag}
                onClose={() => setOpenValue(OptimizationDialogOpenValue.Closed)}
                componentClasses={styles.dialog}
                width={720}
                isGradientBorder
                closeIcon={
                    <CloseWithHelp
                        onHelp={e =>
                            helpPopoverRef.current.showHelpPopover({
                                content:
                                    openValue === OptimizationDialogOpenValue.UnassignedOpen
                                        ? texts.unassigned_optimization_help_text
                                        : texts.route_optimization_help_text,
                                anchor: e.target
                            })
                        }
                        componentClasses={styles.headerIcons}
                    />
                }
                title={
                    <Flex
                        direction={'row'}
                        componentClasses={styles.title}
                        justifyContent={'flex-start'}
                        alignItems={'center'}
                    >
                        <AIStarIcon />
                        <Title skin={'h2'} textColor={'primary-700'}>
                            {openValue === OptimizationDialogOpenValue.Open
                                ? texts.route_optimization_title
                                : texts.unassigned_optimization_title}
                        </Title>
                    </Flex>
                }
                zIndex={zIndex}
                content={
                    <>
                        <UtilizationWarning
                            type={utilizationValue}
                            value={utilizationNumericValue}
                        />
                        <OptimizationDialogContent
                            optimizationDialogState={optimizationDialogState}
                            setOptimizationDialogState={setOptimizationDialogState}
                            openValue={openValue}
                            utilizationValue={utilizationValue}
                            planningForm={planningForm}
                            deliveryDateValue={deliveryDateValue}
                            setDeliveryDateValue={setDeliveryDateValue}
                            selectedRoutes={selectedRoutes.current}
                            zIndex={zIndex}
                        />
                    </>
                }
                footer={
                    <Flex componentClasses={styles.footerButtons} justifyContent={'flex-end'}>
                        <Button
                            dataTestId={DataTestIds.cancelButton}
                            wideButton={true}
                            skin={'secondary'}
                            size={'medium'}
                            onClick={() => setOpenValue(OptimizationDialogOpenValue.Closed)}
                        >
                            {texts.cancel}
                        </Button>
                        <Button
                            dataTestId={DataTestIds.runButton}
                            wideButton={true}
                            skin={'primary'}
                            size={'medium'}
                            onClick={() => !isRunDisabled && onRunButtonClick()}
                            disabled={isRunDisabled}
                        >
                            {openValue === OptimizationDialogOpenValue.Open
                                ? texts.route_optimization_run
                                : texts.unassigned_optimization_run}
                        </Button>
                    </Flex>
                }
            />
        ) : null;
    }
);
